import { useEffect, useMemo, useState } from "react";
import { CheckCircle, FileText, Gauge, ListChecks, Plus, RocketLaunch, X, ArrowRight, Sparkle, PencilSimple } from "@phosphor-icons/react";
import axios from "axios";
import { formatErr } from "@/lib/api";
import { toast } from "sonner";
import ContentEngineDraftEditor from "@/components/ContentEngineDraftEditor";

// Content Engine V1 is hosted on the production Render backend. The rest of
// RankTrust intentionally retains its existing Preview3 API routing.
const contentApi = axios.create({
  baseURL: `${process.env.REACT_APP_CONTENT_ENGINE_BACKEND_URL || "https://ranktrust.onrender.com"}/api`,
  withCredentials: true,
});
contentApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const stages = [
  { id: "opportunity", label: "Opportunities", icon: Gauge },
  { id: "queued", label: "Content Queue", icon: ListChecks },
  { id: "draft", label: "Drafts", icon: FileText },
  { id: "approved", label: "Approved", icon: CheckCircle },
  { id: "published", label: "Published", icon: RocketLaunch },
];

const blankOpportunity = {
  target_query: "",
  target_audience: "service business owners",
  intent: "informational",
  opportunity_reason: "",
  brief: "",
};

// Import supports one keyword per line or CSV with keyword/priority columns.
function parseCsvRow(line) {
  const cells = []; let cell = ""; let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (quoted && line[i + 1] === '"') { cell += '"'; i += 1; }
      else quoted = !quoted;
    } else if (ch === "," && !quoted) { cells.push(cell.trim()); cell = ""; }
    else cell += ch;
  }
  cells.push(cell.trim());
  return cells;
}

function parseKeywords(text) {
  const lines = text.split(/\r?\n/).map((v) => v.trim()).filter(Boolean);
  if (!lines.length) return [];
  const header = parseCsvRow(lines[0]).map((v) => v.toLowerCase().replace(/[^a-z0-9]/g, "_"));
  const keywordCol = header.findIndex((v) => ["keyword", "target_query", "search_query", "search_term"].includes(v));
  const priorityCol = header.findIndex((v) => ["priority", "tier"].includes(v));
  const items = []; const seen = new Set();
  for (const line of (keywordCol >= 0 ? lines.slice(1) : lines)) {
    const cols = parseCsvRow(line);
    const prefixed = line.match(/^(P[123])\s*[,|\t:]\s*(.+)$/i);
    const target_query = (keywordCol >= 0 ? (cols[keywordCol] || "") : prefixed ? prefixed[2] : line).trim();
    const rawPriority = keywordCol >= 0 ? cols[priorityCol] : prefixed?.[1];
    const priority = /^P[123]$/i.test(rawPriority || "") ? rawPriority.toUpperCase() : "P1";
    const key = target_query.toLowerCase().replace(/\s+/g, " ");
    if (target_query.length >= 2 && target_query.length <= 180 && !seen.has(key)) {
      items.push({ target_query, priority }); seen.add(key);
    }
  }
  return items;
}

export default function ContentEngine() {
  const [activeStage, setActiveStage] = useState("opportunity");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(blankOpportunity);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState("");
  const [importAudience, setImportAudience] = useState("service business owners");
  const [importBusy, setImportBusy] = useState(false);
  const [importProgress, setImportProgress] = useState("");
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchProgress, setBatchProgress] = useState("");


  const load = async () => {
    try {
      const { data } = await contentApi.get("/content-engine");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const visible = useMemo(() => items.filter((item) => item.status === activeStage), [items, activeStage]);

  const replaceItem = (data) => setItems((current) => current.map((x) => x.id === data.id ? data : x));

  const createOpportunity = async (e) => {
    e.preventDefault();
    try {
      const { data } = await contentApi.post("/content-engine/opportunities", form);
      setItems((current) => [data, ...current]);
      setForm(blankOpportunity);
      setShowCreate(false);
      setActiveStage("opportunity");
      toast.success("Opportunity added");
    } catch (e2) { toast.error(formatErr(e2)); }
  };

  const importKeywords = async (e) => {
    e.preventDefault();
    const parsed = parseKeywords(importText);
    if (!parsed.length) { toast.error("Paste keywords or choose a valid keyword file."); return; }
    setImportBusy(true);
    let imported = 0; let skipped = 0;
    try {
      for (let i = 0; i < parsed.length; i += 100) {
        const batch = parsed.slice(i, i + 100);
        setImportProgress(String(Math.min(i + 100, parsed.length)) + " of " + parsed.length + " keywords");
        const { data } = await contentApi.post("/content-engine/opportunities/import-keywords", {
          items: batch, target_audience: importAudience,
        });
        imported += data.created;
        skipped += data.duplicates_skipped;
        setItems((current) => [...data.items, ...current]);
      }
      setImportText("");
      setShowImport(false);
      setActiveStage("queued");
      toast.success(imported + " keywords queued; " + skipped + " duplicates skipped. No AI credits used.");
    } catch (error) {
      toast.error("Stopped after " + imported + " additions: " + formatErr(error) + ". Reimport safely; duplicates are skipped.");
    } finally { setImportBusy(false); setImportProgress(""); }
  };

  const generateNextThree = async () => {
    const priorityRank = { P1: 0, P2: 1, P3: 2 };
    const selected = items.filter((item) => item.status === "queued")
      .sort((a, b) => ((priorityRank[a.priority] ?? 3) - (priorityRank[b.priority] ?? 3)) || String(a.created_at || "").localeCompare(String(b.created_at || "")))
      .slice(0, 3);
    if (!selected.length) return;
    setBatchBusy(true);
    let completed = 0;
    try {
      // Browser-driven sequential requests avoid burst spend and server timeouts.
      for (const item of selected) {
        setBatchProgress(String(completed + 1) + "/" + selected.length + ": " + item.target_query);
        const { data } = await contentApi.post("/content-engine/" + item.id + "/generate");
        replaceItem(data);
        completed += 1;
      }
      setActiveStage("draft");
      toast.success(completed + " drafts generated. Verify citations before approval.");
    } catch (error) {
      toast.error("Stopped after " + completed + " drafts: " + formatErr(error));
      if (completed > 0) setActiveStage("draft");
    } finally { setBatchBusy(false); setBatchProgress(""); }
  };

  const runAction = async (item, action) => {
    setBusyId(item.id);
    try {
      if (action === "approve-publish") {
        // One explicit editorial action, two audited server transitions.
        // On publish failure the article stays Approved and can be retried.
        const { data: approved } = await contentApi.post("/content-engine/" + item.id + "/approve");
        replaceItem(approved);
        setActiveStage("approved");
        const { data: published } = await contentApi.post("/content-engine/" + item.id + "/publish");
        replaceItem(published);
        setActiveStage("published");
        toast.success("Approved and published. Sitemap updates automatically.");
        return;
      }
      const { data } = await contentApi.post(`/content-engine/${item.id}/${action}`);
      replaceItem(data);
      if (action === "queue") setActiveStage("queued");
      if (action === "generate") { setActiveStage("draft"); setEditing(data); }
      if (action === "approve") setActiveStage("approved");
      if (action === "publish") setActiveStage("published");
      const messages = { queue: "Moved to Content Queue", generate: "Draft generated", approve: "Approved", publish: "Published", "quality-check": "Quality check complete" };
      toast.success(messages[action] || "Updated");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusyId(null); }
  };

  const saveDraft = async (values) => {
    if (!editing) return;
    setSaving(true);
    try {
      const { data } = await contentApi.patch(`/content-engine/${editing.id}`, values);
      replaceItem(data);
      setEditing(data);
      setActiveStage("draft");
      toast.success(data.quality_gate?.passed ? "Draft saved — quality gate passed" : "Draft saved — review quality checks");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-6 lg:p-10 space-y-8 max-w-7xl">
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
        <div className="space-y-2">
          <div className="label-tiny">Opportunity Intelligence</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Content Engine</h1>
          <p className="text-zinc-500 max-w-3xl">Turn detected opportunities into useful content. Nothing publishes automatically.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="btn-secondary" onClick={() => setShowImport(true)} disabled={importBusy}>Import Keyword Queue</button>
          <button className="btn-primary" onClick={() => setShowCreate(true)}><Plus size={14} weight="bold" /> Add Opportunity</button>
        </div>
      </header>

      <section className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {stages.map(({ id, label, icon: Icon }) => {
          const count = items.filter((item) => item.status === id).length;
          const active = activeStage === id;
          return <button key={id} onClick={() => setActiveStage(id)} className={`bento p-4 text-left transition ${active ? "ring-2 ring-[#002FA7]" : "hover:bg-zinc-50"}`}>
            <div className="flex items-center justify-between"><Icon size={18} weight={active ? "fill" : "regular"} /><span className="font-mono text-xs text-zinc-500">{count}</span></div>
            <div className="font-bold mt-4">{label}</div>
          </button>;
        })}
      </section>

      <section className="bento overflow-hidden">
        <div className="p-5 border-b border-zinc-200">
          <div className="font-bold">{stages.find((stage) => stage.id === activeStage)?.label}</div>
          <div className="text-xs text-zinc-500 mt-1">Import and SEO checks are automated; source verification and publishing stay human-controlled.</div>
          {activeStage === "queued" && items.some((item) => item.status === "queued") && (
            <div className="mt-4 space-y-2">
              <button className="btn-primary" disabled={batchBusy || busyId != null || importBusy} onClick={generateNextThree}>
                {batchBusy ? "Generating drafts…" : "Generate next 3 drafts (uses OpenAI credits)"}
              </button>
              {batchProgress && <p role="status" className="text-xs text-zinc-600">{batchProgress}</p>}
            </div>
          )}
        </div>

        {loading ? <div className="p-10 text-center text-zinc-500">Loading Content Engine…</div> : visible.length === 0 ? (
          <div className="p-10 text-center text-zinc-500">No items in this stage yet.</div>
        ) : (
          <div className="divide-y divide-zinc-200">
            {visible.map((item) => (
              <article key={item.id} className="p-6 space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="label-tiny">{item.intent || "informational"}</span>
                  <span className="text-xs rounded-full border border-zinc-300 px-2.5 py-1">{item.target_audience}</span>
                  {item.quality_gate?.score != null && <span className={`text-xs rounded-full border px-2.5 py-1 ${item.quality_gate.passed ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700"}`}>Quality {item.quality_gate.score}%</span>}
                </div>
                <div>
                  <h2 className="font-display text-2xl font-bold tracking-tight">{item.title || item.target_query}</h2>
                  <p className="text-sm text-zinc-600 max-w-3xl mt-2">{item.opportunity_reason || item.excerpt}</p>
                </div>
                {item.slug && <div className="font-mono text-xs text-zinc-500">/insights/{item.slug}</div>}
                <div className="flex flex-wrap gap-2 pt-1">
                  {item.status === "opportunity" && <button className="btn-primary" disabled={busyId === item.id} onClick={() => runAction(item, "queue")}>Add to Queue <ArrowRight size={13} /></button>}
                  {item.status === "queued" && <button className="btn-primary" disabled={busyId === item.id} onClick={() => runAction(item, "generate")}>{busyId === item.id ? "Generating…" : "Generate Draft"} <Sparkle size={13} weight="fill" /></button>}
                  {item.status === "draft" && <><button className="btn-secondary" onClick={() => setEditing(item)}>Edit Draft <PencilSimple size={13} /></button><button className="btn-secondary" disabled={busyId === item.id} onClick={() => runAction(item, "quality-check")}>Recheck Quality</button><button className="btn-primary" disabled={busyId === item.id || !item.quality_gate?.passed} onClick={() => runAction(item, "approve")}>Approve <CheckCircle size={13} /></button><button className="btn-primary" disabled={busyId === item.id || batchBusy || !item.quality_gate?.passed} onClick={() => runAction(item, "approve-publish")}>Approve & Publish <RocketLaunch size={13} /></button></>}
                  {item.status === "approved" && <><button className="btn-secondary" onClick={() => setEditing(item)}>Revise <PencilSimple size={13} /></button><button className="btn-primary" disabled={busyId === item.id} onClick={() => runAction(item, "publish")}>Publish <RocketLaunch size={13} /></button></>}
                  {item.status === "published" && item.public_path && <a href={`https://ranktrust.io${item.public_path}`} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-[#002FA7] underline underline-offset-2 hover:text-blue-800" aria-label={`Open published article: ${item.title || item.target_query}`}>View published article: {item.public_path} ↗</a>}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="grid lg:grid-cols-3 gap-4">
        <div className="bento p-5"><div className="label-tiny">Quality Gate</div><div className="font-bold mt-2">Required before approval</div><p className="text-sm text-zinc-500 mt-2">Depth, target-query relevance, metadata, clean slug, headings, assessment CTA, internal links, primary-source citations, editorial source verification, and placeholder detection.</p></div>
        <div className="bento p-5"><div className="label-tiny">Publishing</div><div className="font-bold mt-2">Manual in V1</div><p className="text-sm text-zinc-500 mt-2">Import queues keywords automatically; drafting is on demand. Published pages enter the dynamic sitemap automatically after approval and manual publication.</p></div>
        <div className="bento p-5"><div className="label-tiny">Performance</div><div className="font-bold mt-2">Measure what matters</div><p className="text-sm text-zinc-500 mt-2">Impressions, clicks, assessment visits, and leads are the first stored outcomes.</p></div>
      </section>

      {showImport && <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
        <form onSubmit={importKeywords} className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-lg shadow-xl p-6 space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="font-display text-2xl font-bold">Import Keyword Queue</h2>
            <button type="button" aria-label="Close keyword import" disabled={importBusy} onClick={() => setShowImport(false)}><X size={20}/></button>
          </div>
          <p className="text-sm text-zinc-600">
            Paste prioritized keywords or upload a .txt/.csv file. One keyword per line, optionally
            "P1 | keyword". CSV headers can be "keyword" and "priority". Unmarked keywords default to P1.
          </p>
          <input type="file" accept=".txt,.csv,text/plain,text/csv" disabled={importBusy}
            onChange={async (e) => { const file = e.target.files?.[0]; if (file) setImportText(await file.text()); }} />
          <textarea aria-label="Prioritized keywords" className="input-field w-full min-h-[260px] font-mono text-sm"
            value={importText} onChange={(e) => setImportText(e.target.value)} disabled={importBusy}
            placeholder={"P1 | AI search for HVAC contractors\nP1 | Google Maps visibility for plumbers\nP2 | roofing company local SEO"} />
          <label className="block text-sm font-semibold">Audience
            <input className="input-field w-full mt-1" value={importAudience} maxLength={180}
              onChange={(e) => setImportAudience(e.target.value)} disabled={importBusy}/>
          </label>
          <p className="text-sm text-zinc-500">Keywords import automatically in groups of 100; duplicates are skipped. Import never spends AI credits or publishes articles.</p>
          {importProgress && <p role="status" className="text-sm text-blue-700">{importProgress}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" disabled={importBusy} onClick={() => setShowImport(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={importBusy || !importText.trim()}>{importBusy ? "Importing…" : "Import & Queue"}</button>
          </div>
        </form>
      </div>}

      {showCreate && <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
        <form onSubmit={createOpportunity} className="bg-white w-full max-w-2xl rounded-lg shadow-xl p-6 space-y-4">
          <div className="flex items-center justify-between"><div><div className="label-tiny">Content Engine</div><h2 className="font-display text-2xl font-bold">Add Opportunity</h2></div><button type="button" aria-label="Close" onClick={() => setShowCreate(false)}><X size={20} /></button></div>
          <div><label className="label-tiny block">Target query</label><input className="input-field mt-1 w-full" required value={form.target_query} onChange={(e) => setForm({ ...form, target_query: e.target.value })} /></div>
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className="label-tiny block">Audience</label><input className="input-field mt-1 w-full" value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} /></div>
            <div><label className="label-tiny block">Intent</label><select className="input-field mt-1 w-full" value={form.intent} onChange={(e) => setForm({ ...form, intent: e.target.value })}><option value="informational">Informational</option><option value="commercial">Commercial</option><option value="transactional">Transactional</option><option value="local">Local</option></select></div>
          </div>
          <div><label className="label-tiny block">Why this opportunity exists</label><textarea className="input-field mt-1 w-full min-h-24" value={form.opportunity_reason} onChange={(e) => setForm({ ...form, opportunity_reason: e.target.value })} /></div>
          <div><label className="label-tiny block">Brief</label><textarea className="input-field mt-1 w-full min-h-28" value={form.brief} onChange={(e) => setForm({ ...form, brief: e.target.value })} /></div>
          <div className="flex justify-end gap-2"><button type="button" className="btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button><button className="btn-primary" type="submit">Create Opportunity</button></div>
        </form>
      </div>}

      <ContentEngineDraftEditor item={editing} onClose={() => setEditing(null)} onSave={saveDraft} saving={saving} />
    </div>
  );
}
